import React, { FC, useMemo, useState } from 'react';
import { Radio } from '@bedrock/components';
import { DataSourceTag, FormModule } from '@purchase/supplier';
import DownloadAllButton from '../DownloadAllButton';
import SupplierAttachmentTable from './SupplierAttachmentTable';
import QccCertificateTable from './QccCertificateTable';

export interface RelatedMaterialsProps {
  isExternal?: boolean;
  formDetail?: any;
}

type MaterialSource = 'supplier' | 'thirdParty';

const RelatedMaterials: FC<RelatedMaterialsProps> = ({
  isExternal = false,
  formDetail,
}) => {
  const [materialSource, setMaterialSource] =
    useState<MaterialSource>('supplier');

  const qccCertificates = formDetail?.vendorQccCertificateInfos ?? [];
  const defaultQccCertificates =
    formDetail?.defaultVendorQccCertificateInfos ?? [];
  const qccCertificateUpdateTime = formDetail?.qccDataFetchedAt;
  const hasExtraQccCertificates = qccCertificates.length > 3;

  const allAttachmentsCount = useMemo(
    () => formDetail?.vendorAttachmentInfo?.otherAttachments?.length ?? 0,
    [formDetail?.vendorAttachmentInfo?.otherAttachments]
  );
  const defaultAttachmentsCount = useMemo(
    () =>
      formDetail?.vendorAttachmentInfo?.defaultOtherAttachments?.length ?? 0,
    [formDetail?.vendorAttachmentInfo?.defaultOtherAttachments]
  );
  const hasExtraAttachments = allAttachmentsCount > defaultAttachmentsCount;

  const supplierMaterialsCount = allAttachmentsCount;
  const thirdPartyCount = qccCertificates.length;
  const totalCount = isExternal
    ? supplierMaterialsCount
    : supplierMaterialsCount + thirdPartyCount;

  const showSupplierTable = isExternal || materialSource === 'supplier';
  const showQccTable = !isExternal && materialSource === 'thirdParty';

  return (
    <FormModule
      title="相关材料"
      titleCount={totalCount}
      proCardProps={{
        expandable: true,
        defaultExpanded: totalCount > 0,
        rightSlot: showSupplierTable ? (
          <DownloadAllButton formDetail={formDetail} isExternal={isExternal} />
        ) : null,
      }}
    >
      {!isExternal && (
        <div className="flex items-center justify-between mb-3">
          <Radio.Group
            value={materialSource}
            onChange={(e) => setMaterialSource(e.target.value)}
          >
            <Radio.Button value="supplier">
              供应商录入 ({supplierMaterialsCount})
            </Radio.Button>
            <Radio.Button value="thirdParty">
              企查查获取 ({thirdPartyCount})
            </Radio.Button>
          </Radio.Group>
          {materialSource === 'thirdParty' && (
            <DataSourceTag
              source="企查查"
              updateTime={qccCertificateUpdateTime}
            />
          )}
        </div>
      )}

      {showSupplierTable && (
        <SupplierAttachmentTable hasExtra={hasExtraAttachments} />
      )}

      {showQccTable && (
        <QccCertificateTable
          list={qccCertificates}
          defaultList={defaultQccCertificates}
          hasExtra={hasExtraQccCertificates}
        />
      )}
    </FormModule>
  );
};

export default RelatedMaterials;
